import { type FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { adminApi, type Account, type Invite } from "../api/admin";
import { ApiError } from "../api/client";
import { Alert, Button, Card, Field, Spinner } from "../components/ui";
import { useCurrentUser } from "../hooks/useAuth";

const KEY = ["admin", "invites"];

/**
 * Mint and withdraw invite codes.
 *
 * Administrator-only, and the server enforces that independently — hiding the
 * screen is a convenience, not the access control.
 */
export default function Invites() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const [uses, setUses] = useState(1);
  const [days, setDays] = useState(14);
  const [justMinted, setJustMinted] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({ queryKey: KEY, queryFn: adminApi.listInvites });

  const create = useMutation({
    mutationFn: () => adminApi.createInvite(uses, days),
    onSuccess: (invite) => {
      setJustMinted(invite.code);
      queryClient.invalidateQueries({ queryKey: KEY });
    },
  });

  const revoke = useMutation({
    mutationFn: (id: string) => adminApi.revokeInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });

  if (!user?.is_admin) {
    return <Alert kind="error">This page is for administrators.</Alert>;
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    create.mutate();
  }

  const message =
    create.error instanceof ApiError
      ? create.error.message
      : revoke.error instanceof ApiError
        ? revoke.error.message
        : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Invites</h1>
        <p className="mt-1 text-sm text-gray-600">
          There is no open registration — everyone needs a code from you. They spend your API
          credit, capped per person.
        </p>
      </header>

      <Card title="Mint a code">
        <form onSubmit={onSubmit} className="space-y-4">
          {message && <Alert kind="error">{message}</Alert>}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="How many people can use it">
              <select
                value={uses}
                onChange={(e) => setUses(Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 bg-surface px-3 py-2 text-sm"
              >
                <option value={1}>1 person</option>
                <option value={3}>3 people</option>
                <option value={5}>5 people</option>
                <option value={10}>10 people</option>
              </select>
            </Field>

            <Field label="Expires after">
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 bg-surface px-3 py-2 text-sm"
              >
                <option value={1}>1 day</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={90}>90 days</option>
                <option value={0}>Never</option>
              </select>
            </Field>
          </div>

          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Minting…" : "Mint invite code"}
          </Button>
        </form>

        {justMinted && <MintedCode code={justMinted} />}
      </Card>

      <Card title="All codes">
        {isPending && <Spinner label="Loading codes…" />}
        {error && <Alert kind="error">Could not load invite codes.</Alert>}
        {data && data.length === 0 && (
          <p className="text-sm text-gray-500">No codes yet.</p>
        )}
        {data && data.length > 0 && (
          <ul className="divide-y divide-gray-200">
            {data.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                onRevoke={() => revoke.mutate(invite.id)}
                busy={revoke.isPending}
              />
            ))}
          </ul>
        )}
      </Card>

      <Accounts currentUserId={user.id} />
    </div>
  );
}

const USERS_KEY = ["admin", "users"];

function Accounts({ currentUserId }: { currentUserId: string }) {
  const queryClient = useQueryClient();
  const { data, isPending, error } = useQuery({
    queryKey: USERS_KEY,
    queryFn: adminApi.listUsers,
  });

  const remove = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY }),
  });

  function onDelete(account: Account) {
    const applications =
      account.application_count === 1 ? "1 application" : `${account.application_count} applications`;
    if (
      confirm(
        `Permanently delete ${account.display_name} (${account.email})?\n\n` +
          `This removes their profile, templates and ${applications}, ` +
          "including every generated document. It cannot be undone.",
      )
    ) {
      remove.mutate(account.id);
    }
  }

  return (
    <Card title="Accounts">
      {remove.error instanceof ApiError && <Alert kind="error">{remove.error.message}</Alert>}
      {isPending && <Spinner label="Loading accounts…" />}
      {error && <Alert kind="error">Could not load accounts.</Alert>}
      {data && (
        <ul className="divide-y divide-gray-200">
          {data.map((account) => (
            <li key={account.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {account.display_name}
                </p>
                <p className="truncate text-xs text-gray-500">{account.email}</p>
              </div>

              <span className="text-xs text-gray-500">
                {account.application_count}{" "}
                {account.application_count === 1 ? "application" : "applications"} · joined{" "}
                {new Date(account.created_at).toLocaleDateString()}
              </span>

              <span className="ms-auto flex items-center gap-3">
                {account.is_admin && (
                  <span className="rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700">
                    Admin
                  </span>
                )}
                {account.id === currentUserId ? (
                  <span className="text-xs text-gray-400">You</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onDelete(account)}
                    disabled={remove.isPending}
                    className="text-sm text-red-600 hover:underline disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MintedCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the code is on screen to read anyway.
    }
  }

  return (
    <div className="mt-5 rounded-md border border-accent-100 bg-accent-50 p-4">
      <p className="text-xs font-medium text-accent-700">Send this to whoever is joining</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <code className="font-mono text-lg tracking-wider text-gray-900">{code}</code>
        <Button variant="secondary" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-gray-600">
        They open this site, choose <strong>Create an account</strong>, and enter it.
      </p>
    </div>
  );
}

function InviteRow({
  invite,
  onRevoke,
  busy,
}: {
  invite: Invite;
  onRevoke: () => void;
  busy: boolean;
}) {
  const expires = invite.expires_at
    ? new Date(invite.expires_at).toLocaleDateString()
    : "never";

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
      <code
        className={
          "font-mono text-sm tracking-wide " +
          (invite.is_usable ? "text-gray-900" : "text-gray-400 line-through")
        }
      >
        {invite.code}
      </code>

      <span className="text-xs text-gray-500">
        {invite.uses}/{invite.max_uses} used · expires {expires}
        {invite.created_by_name ? ` · by ${invite.created_by_name}` : " · from the CLI"}
      </span>

      <span className="ms-auto flex items-center gap-3">
        {invite.is_usable ? (
          <>
            <span className="rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700">
              Active
            </span>
            <button
              type="button"
              onClick={onRevoke}
              disabled={busy}
              className="text-sm text-red-600 hover:underline disabled:opacity-50"
            >
              Revoke
            </button>
          </>
        ) : (
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500">
            {invite.uses >= invite.max_uses ? "Used up" : "Expired"}
          </span>
        )}
      </span>
    </li>
  );
}
